import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MessageSquare, Send, Trash2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const SmsCampaignsPage = () => {
  const [items, setItems] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', message: '', campaign_type: 'general', target_audience: 'all_clients', target_office_id: '', scheduled_date: '' });

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const [campRes, offRes] = await Promise.all([api.get('/sms-campaigns'), api.get('/offices')]);
      setItems(campRes.data);
      setOffices(offRes.data);
    } catch { setItems([]); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.message) { toast.error('Name and message are required'); return; }
    try { await api.post('/sms-campaigns', form); toast.success('Campaign created'); setShowModal(false); setForm({ name: '', message: '', campaign_type: 'general', target_audience: 'all_clients', target_office_id: '', scheduled_date: '' }); load(); }
    catch { toast.error('Failed to create campaign'); }
  };

  const handleSend = async (id) => {
    if (!window.confirm('Send this campaign now?')) return;
    try { const res = await api.post(`/sms-campaigns/${id}/send`); toast.success(res.data.message); load(); }
    catch { toast.error('Failed to send'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try { await api.delete(`/sms-campaigns/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const statusStyles = { draft: 'bg-gray-100 text-gray-700', scheduled: 'bg-blue-50 text-blue-700', sent: 'bg-emerald-50 text-emerald-700', failed: 'bg-red-50 text-red-700' };
  const typeLabels = { general: 'General', loan_reminder: 'Loan Reminder', marketing: 'Marketing', alert: 'Alert' };

  return (
    <AdminSubPage title="SMS Campaigns" breadcrumb={['Admin', 'Organization', 'SMS Campaigns']} backPath="/dashboard/admin/organization" actionLabel="+ New Campaign" onAction={() => setShowModal(true)}>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-2">
        <div className="p-4 border border-gray-200 bg-white"><p className="text-xs uppercase tracking-wider text-gray-500">Total</p><p className="text-2xl font-bold text-[#1f1f1f] mt-1">{items.length}</p></div>
        <div className="p-4 border border-gray-200 bg-white"><p className="text-xs uppercase tracking-wider text-gray-500">Draft</p><p className="text-2xl font-bold text-gray-600 mt-1">{items.filter(i => i.status === 'draft').length}</p></div>
        <div className="p-4 border border-gray-200 bg-white"><p className="text-xs uppercase tracking-wider text-gray-500">Sent</p><p className="text-2xl font-bold text-emerald-600 mt-1">{items.filter(i => i.status === 'sent').length}</p></div>
        <div className="p-4 border border-gray-200 bg-white"><p className="text-xs uppercase tracking-wider text-gray-500">Total Sent</p><p className="text-2xl font-bold text-[#C5A059] mt-1">{items.reduce((s, i) => s + (i.sent_count || 0), 0)}</p></div>
      </div>
      <div className="bg-white border border-gray-200" data-testid="sms-campaigns-table">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Campaign</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Type</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Target</th>
            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Sent</th>
            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            items.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-gray-400"><MessageSquare className="w-8 h-8 mx-auto mb-2 text-gray-300" />No SMS campaigns yet.</td></tr> :
            items.map(item => (
              <tr key={item.id} className="border-b hover:bg-gray-50" data-testid={`campaign-row-${item.id}`}>
                <td className="px-4 py-3"><div className="font-medium text-sm text-[#1f1f1f]">{item.name}</div><div className="text-xs text-gray-400 max-w-[200px] truncate">{item.message}</div></td>
                <td className="px-4 py-3 text-sm text-gray-600">{typeLabels[item.campaign_type] || item.campaign_type}</td>
                <td className="px-4 py-3 text-sm text-gray-600 capitalize">{(item.target_audience || '').replace(/_/g, ' ')}</td>
                <td className="px-4 py-3 text-center text-sm font-mono">{item.sent_count || 0}</td>
                <td className="px-4 py-3 text-center"><span className={`px-2 py-0.5 text-xs font-medium capitalize ${statusStyles[item.status] || 'bg-gray-100'}`}>{item.status}</span></td>
                <td className="px-4 py-3 flex gap-1">
                  {item.status === 'draft' && <Button variant="ghost" size="sm" onClick={() => handleSend(item.id)} className="text-emerald-600" data-testid={`send-campaign-${item.id}`}><Send className="w-4 h-4" /></Button>}
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg" aria-describedby="sms-desc"><DialogHeader><DialogTitle>Create SMS Campaign</DialogTitle></DialogHeader>
          <p id="sms-desc" className="sr-only">SMS campaign form</p>
          <div className="space-y-4">
            <div><Label>Campaign Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="campaign-name-input" /></div>
            <div><Label>Message *</Label><Textarea rows={3} value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="Dear {client_name}, your loan payment of R{amount} is due on {date}..." data-testid="campaign-message-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Campaign Type</Label>
                <Select value={form.campaign_type} onValueChange={v => setForm({...form, campaign_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="loan_reminder">Loan Reminder</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="alert">Alert</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Target Audience</Label>
                <Select value={form.target_audience} onValueChange={v => setForm({...form, target_audience: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_clients">All Clients</SelectItem>
                    <SelectItem value="by_office">By Office</SelectItem>
                    <SelectItem value="active_borrowers">Active Borrowers</SelectItem>
                    <SelectItem value="overdue_borrowers">Overdue Borrowers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.target_audience === 'by_office' && (
              <div><Label>Target Office</Label>
                <Select value={form.target_office_id} onValueChange={v => setForm({...form, target_office_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select office" /></SelectTrigger>
                  <SelectContent>{offices.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Scheduled Date (optional)</Label><Input type="datetime-local" value={form.scheduled_date} onChange={e => setForm({...form, scheduled_date: e.target.value})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="campaign-submit-btn">Create Campaign</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminSubPage>
  );
};
export default SmsCampaignsPage;
